<template>
  <div class="card-chart">
    <!-- Card Header -->
    <div class="card-header">
      <div class="card-header-left">
        <h3 class="card-title">{{ title }}</h3>
        <span v-if="subtitle" class="card-subtitle">{{ subtitle }}</span>
      </div>
      <div class="card-header-right">
        <slot name="controls"></slot>
      </div>
    </div>

    <!-- Card Body -->
    <div class="card-body">
      <div class="chart-container" ref="chartContainer" @mousemove="handleMouseMove" @click="handleContainerClick">
        <v-chart ref="chart" v-if="chartOptions" :option="chartOptions" :autoresize="true" @click="handleChartClick" />
        <div v-else class="chart-empty">No data available</div>
      </div>
    </div>
  </div>
</template>

<script>
import { use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { BarChart } from 'echarts/charts';
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent
} from 'echarts/components';
import VChart from 'vue-echarts';

use([
  CanvasRenderer,
  BarChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent
]);

export default {
  name: 'CardBarChart',
  components: { VChart },
  emits: ['bar-click'],
  data() {
    return {
      currentMousePos: null, // For stacked bars: X position (horizontal) or Y position (vertical)
      clickHandledByChart: false
    };
  },
  props: {
    title: {
      type: String,
      required: true
    },
    subtitle: {
      type: String,
      default: ''
    },
    data: {
      type: Object,
      default: null
    },
    variant: {
      type: String,
      default: 'simple',
      validator: (v) => ['simple', 'stacked', 'grouped'].includes(v)
    },
    options: {
      type: Object,
      default: () => ({})
    },
    height: {
      type: Number,
      default: 300
    },
    showLegend: {
      type: Boolean,
      default: true
    },
    horizontal: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    isStacked() {
      return this.variant === 'stacked';
    },
    chartOptions() {
      if (!this.data || !this.data.labels || !this.data.datasets || this.data.datasets.length === 0) {
        return null;
      }

      // Primary indigo palette - ordered for contrast in stacked charts (dark/light alternating)
      const colors = ['#4338ca', '#a5b4fc', '#3730a3', '#c7d2fe', '#4f46e5', '#818cf8', '#312e81', '#6366f1'];

      const series = this.data.datasets.map((dataset, index) => ({
        name: dataset.label,
        type: 'bar',
        stack: this.isStacked ? 'total' : undefined,
        data: dataset.data,
        itemStyle: {
          color: dataset.color || colors[index % colors.length],
          borderRadius: 0
        },
        barMaxWidth: 40,
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: 'rgba(0, 0, 0, 0.2)'
          }
        }
      }));

      const baseOptions = {
        color: colors,
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          },
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          borderColor: 'transparent',
          textStyle: {
            color: '#fff',
            fontSize: 12
          },
          padding: 12
        },
        legend: {
          show: this.showLegend && this.data.datasets.length > 1,
          bottom: 0,
          left: 'center',
          icon: 'circle',
          itemWidth: 8,
          itemHeight: 8,
          textStyle: {
            fontSize: 12,
            color: '#666'
          }
        },
        grid: {
          left: 12,
          right: 12,
          bottom: this.showLegend && this.data.datasets.length > 1 ? 40 : 12,
          top: 12,
          containLabel: true
        },
        xAxis: {
          type: this.horizontal ? 'value' : 'category',
          data: this.horizontal ? undefined : this.data.labels,
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: {
            color: '#666',
            fontSize: 11
          },
          splitLine: {
            show: this.horizontal,
            lineStyle: { color: '#f0f0f0' }
          }
        },
        yAxis: {
          type: this.horizontal ? 'category' : 'value',
          data: this.horizontal ? this.data.labels : undefined,
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: {
            color: '#666',
            fontSize: 11
          },
          splitLine: {
            show: !this.horizontal,
            lineStyle: { color: '#f0f0f0' }
          }
        },
        series
      };

      // Only merge options if they exist and are not null
      if (!this.options || Object.keys(this.options).length === 0) {
        return baseOptions;
      }

      return this.deepMerge(baseOptions, this.options);
    }
  },
  methods: {
    handleMouseMove(event) {
      if (!this.isStacked) return;

      const chart = this.$refs.chart;
      if (!chart) return;

      const echartsInstance = chart.chart;
      if (!echartsInstance) return;

      const rect = this.$refs.chartContainer.getBoundingClientRect();

      try {
        if (this.horizontal) {
          // For horizontal bars, track X position (value axis)
          const pixelX = event.clientX - rect.left;
          const point = echartsInstance.convertFromPixel('grid', [pixelX, 0]);
          if (point && point[0] !== undefined) {
            this.currentMousePos = point[0];
          }
        } else {
          // For vertical bars, track Y position (value axis)
          const pixelY = event.clientY - rect.top;
          const point = echartsInstance.convertFromPixel('grid', [0, pixelY]);
          if (point && point[1] !== undefined) {
            this.currentMousePos = point[1];
          }
        }
      } catch (e) {
        // Chart not ready yet
      }
    },

    findHoveredSeries(dataIndex) {
      if (this.currentMousePos === null) {
        // Default to the last series (topmost in stack)
        return this.data.datasets[this.data.datasets.length - 1];
      }

      // Calculate cumulative values to find which "band" we're in
      let cumulative = 0;

      for (let i = 0; i < this.data.datasets.length; i++) {
        const dataset = this.data.datasets[i];
        const value = dataset.data[dataIndex] || 0;
        const nextCumulative = cumulative + value;

        // Check if currentMousePos falls within this band
        if (this.currentMousePos >= cumulative && this.currentMousePos <= nextCumulative) {
          return dataset;
        }
        cumulative = nextCumulative;
      }

      // If beyond all stacks, return the topmost series
      if (this.currentMousePos > cumulative) {
        return this.data.datasets[this.data.datasets.length - 1];
      }

      // If below zero, return bottom series
      return this.data.datasets[0];
    },

    handleChartClick(params) {
      // Mark that this click was handled by the chart
      this.clickHandledByChart = true;
      setTimeout(() => { this.clickHandledByChart = false; }, 100);

      if (params.componentType === 'series') {
        // For stacked bars, ALWAYS use position-based detection
        if (this.isStacked) {
          const chart = this.$refs.chart;
          if (chart && chart.chart) {
            const echartsInstance = chart.chart;

            // Get pixel position from the ECharts event
            const event = params.event?.event || params.event;
            if (event) {
              const rect = this.$refs.chartContainer.getBoundingClientRect();
              const pixelX = (event.clientX || event.zrX || 0) - rect.left;
              const pixelY = (event.clientY || event.zrY || 0) - rect.top;

              try {
                const point = echartsInstance.convertFromPixel('grid', [pixelX, pixelY]);
                if (point) {
                  if (this.horizontal) {
                    // For horizontal bars: X axis is value
                    this.currentMousePos = point[0];
                  } else {
                    // For vertical bars: Y axis is value
                    this.currentMousePos = point[1];
                  }
                }
              } catch (e) {
                // Use tracked mouse position as fallback
              }
            }

            // Find which series band the click was in
            if (this.currentMousePos !== null) {
              const dataIndex = params.dataIndex;
              const hoveredDataset = this.findHoveredSeries(dataIndex);

              if (hoveredDataset) {
                this.$emit('bar-click', {
                  dataIndex: dataIndex,
                  seriesName: hoveredDataset.label,
                  value: hoveredDataset.data[dataIndex],
                  name: params.name
                });
                return;
              }
            }
          }
        }

        // Default behavior for non-stacked or if detection fails
        this.$emit('bar-click', {
          dataIndex: params.dataIndex,
          seriesName: params.seriesName,
          value: params.value,
          name: params.name
        });
      }
    },

    handleContainerClick(event) {
      // Only handle clicks on the bar area for stacked charts
      // If the chart already handled the click, skip
      if (this.clickHandledByChart) return;
      if (!this.isStacked) return;

      const chart = this.$refs.chart;
      if (!chart || !chart.chart) return;

      const echartsInstance = chart.chart;
      const rect = this.$refs.chartContainer.getBoundingClientRect();
      const pixelX = event.clientX - rect.left;
      const pixelY = event.clientY - rect.top;

      try {
        // Convert pixel position to data position
        const point = echartsInstance.convertFromPixel('grid', [pixelX, pixelY]);
        if (!point || point[0] === undefined || point[1] === undefined) return;

        let dataIndex, valuePos;
        if (this.horizontal) {
          // For horizontal bars: Y axis is category, X axis is value
          dataIndex = Math.round(point[1]);
          valuePos = point[0];
        } else {
          // For vertical bars: X axis is category, Y axis is value
          dataIndex = Math.round(point[0]);
          valuePos = point[1];
        }

        if (dataIndex < 0 || dataIndex >= this.data.labels.length) return;

        // Use the value position to find which series band was clicked
        this.currentMousePos = valuePos;
        const hoveredDataset = this.findHoveredSeries(dataIndex);

        if (hoveredDataset) {
          this.$emit('bar-click', {
            dataIndex: dataIndex,
            seriesName: hoveredDataset.label,
            value: hoveredDataset.data[dataIndex],
            name: this.data.labels[dataIndex]
          });
        }
      } catch (e) {
        // Chart not ready or click outside grid area
      }
    },
    deepMerge(target, source) {
      const output = { ...target };
      if (this.isObject(target) && this.isObject(source)) {
        Object.keys(source).forEach(key => {
          if (this.isObject(source[key])) {
            if (!(key in target)) {
              Object.assign(output, { [key]: source[key] });
            } else {
              output[key] = this.deepMerge(target[key], source[key]);
            }
          } else {
            Object.assign(output, { [key]: source[key] });
          }
        });
      }
      return output;
    },
    isObject(item) {
      return item && typeof item === 'object' && !Array.isArray(item);
    }
  }
}
</script>

<style scoped>
.card-chart {
  background: #FFF;
  overflow: hidden;
}

.card-header {
  padding: 16px;
  background-color: var(--content-header-bg);
  border-bottom: var(--content-header-border);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.card-header-left {
  display: flex;
  align-items: baseline;
  gap: 12px;
}

.card-header-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.card-title {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #262626;
}

.card-subtitle {
  font-size: 13px;
  font-weight: 400;
  color: #666;
}

.card-body {
  padding: 16px;
}

.chart-container {
  position: relative;
  height: v-bind('height + "px"');
}

.chart-container .v-chart {
  width: 100%;
  height: 100%;
}

.chart-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #9ca3af;
  font-size: 14px;
}
</style>
